import React, {Component} from "react";

import {connect} from "react-redux";
import {IRootState} from "../store";
import DispatchProps from "../utils/DispatchProps";
import {SchoolSchedule} from "../models/SchoolSchedule";
import {CRON_DROPDOWN_OPTIONS, CRON_OPTIONS, FRIENDLY_DATETIME_FORMAT, INDEXED_CRON_OPTIONS} from "../utils/Utils";

import moment from "moment";
import {
    CommandBarButton,
    Dropdown,
    DropdownMenuItemType, IconButton,
    IDropdownOption,
    MessageBarType,
    Toggle
} from "office-ui-fabric-react";
import ApiService from "./ApiService";
import Actions from "../redux/Actions";
import School from "../models/School";
import TableCell from "./TableCell";

const createBlankSchedule = (school?: School): SchoolSchedule => {

    return {
        tenantName: school ? school.tenantName! : "",
        schoolCode: school ? school.schoolCode! : "",
        cronSchedule: CRON_OPTIONS[0].cronString,
        name: "",
        isPlaylist: false,
        enabled: false,
    }
};

interface SchoolScheduleListRowProps
{
    school: School;
    schedule?: SchoolSchedule;
    apiService: ApiService;
    workloadAndPlaylistOptions: IDropdownOption[];
    namesWithExistingSchedules: string[];
    className?: string;

    onSave(): Promise<void>;
}

interface SchoolScheduleListRowState
{
    editEnabled: boolean;
    saveInProgress: boolean;
    editedSchedule?: SchoolSchedule;
 
    isNewSchedule: boolean;
    nameOptions: IDropdownOption[]
}

class SchoolScheduleListRow extends Component<SchoolScheduleListRowProps & DispatchProps,SchoolScheduleListRowState>
{
    
    constructor(props: Readonly<SchoolScheduleListRowProps & DispatchProps>) {
        super(props);


        this.state = {
            editEnabled: false,
            saveInProgress: false,
            editedSchedule: undefined,
            isNewSchedule: false,
            nameOptions: []
        };
     
    }
    
    load()
    {
        const nameOptions = this.props.workloadAndPlaylistOptions.filter((option: IDropdownOption) => {

            if(option.itemType === DropdownMenuItemType.Header)
            {
                return true;
            }

            const name = option.data?.name;
            if(name)
            {
                return this.props.namesWithExistingSchedules.indexOf(name) === -1
            }
            else
            {
                return false;
            }
        });

        this.setState({
            editEnabled: !this.props.schedule,
            saveInProgress: false,
            editedSchedule: this.props.schedule === undefined ? createBlankSchedule(this.props.school) : Object.assign({},this.props.schedule),
            isNewSchedule: this.props.schedule === undefined,
            nameOptions: nameOptions,
        });
    }

    public componentDidMount() {
        this.load();
    }
    
    componentDidUpdate(prevProps: Readonly<SchoolScheduleListRowProps & DispatchProps>, prevState: Readonly<SchoolScheduleListRowState>, snapshot?: any): void {


        if(this.props.schedule !== prevProps.schedule 
            || this.props.namesWithExistingSchedules !== prevProps.namesWithExistingSchedules 
            || this.props.workloadAndPlaylistOptions !== prevProps.workloadAndPlaylistOptions)
        {
            this.load();
        }
    }
    
    private buildNameCell() : JSX.Element
    {
        if(this.state.isNewSchedule)
        {
            return (
                <div className="cdb-table__cell flex-item--flex-8 flex-column">
                    <Dropdown
                        selectedKey={this.state.editedSchedule!.name}
                        options={this.state.nameOptions}
                        onChange={(e,option) => {
                            if(option)
                            {
                                const newSchedule = Object.assign({},this.state.editedSchedule,{name: option.data.name, isPlaylist: option.data.isPlaylist});
                                this.setState({
                                    editedSchedule: newSchedule
                                });
                            }
                        }}
                    />
                </div>
            );
        }
        else
        {
            return (<div className="cdb-table__cell flex-item--flex-8">{this.state.editedSchedule!.isPlaylist ? "[Playlist] " : null}{this.state.editedSchedule!.name}</div>);
        }
    }
        
    private buildCronStringCell() : JSX.Element
    {
        if(this.state.editEnabled)
        {
            return (
                <div className="cdb-table__cell flex-item--flex-8 flex-column">
                    <Dropdown
                    selectedKey={this.state.editedSchedule ? this.state.editedSchedule!.cronSchedule : ""}
                    options={CRON_DROPDOWN_OPTIONS}
                    onChange={(e,option) => {
                        if(option)
                        {
                            const newSchedule = Object.assign({},this.state.editedSchedule,{cronSchedule: option.data.cronString});
                            this.setState({
                                editedSchedule: newSchedule
                            });
                        }
                    }}
                    />
                </div>
            );
        }
        else
        {
            return (
                <div className="cdb-table__cell flex-item--flex-8 flex-column">{INDEXED_CRON_OPTIONS[this.state.editedSchedule!.cronSchedule].desc}
                    <span className="cdb-table__extra-detail">{this.state.editedSchedule!.cronSchedule}</span>
                </div>);
        }
    }
    
    private buildLastExecutionCell() : JSX.Element
    {
        if(this.state.editedSchedule!.lastExecutionInstanceId)
        {
            return (
                <TableCell className="flex-item--flex-8" to={`/cdb-admin/workloads/${this.state.editedSchedule!.lastExecutionInstanceId}`} enableLink={true}>
                    {moment(this.state.editedSchedule!.lastQueuedForExecutionAt).fromNow()}
                    <span className="cdb-table__extra-detail">{moment(this.state.editedSchedule!.lastQueuedForExecutionAt).format(FRIENDLY_DATETIME_FORMAT)}</span>
                </TableCell>
            );
        }
        else
        {
            return (<div className="cdb-table__cell flex-item--flex-8 flex-column">Never</div>);
        }
    }
    
    private buildNextExecutionCell() : JSX.Element
    {
        if(this.state.editEnabled)
        {
            return (<div className={"cdb-table__cell flex-item--flex-8 flex-column"}/>);
        }
        else
        {
            return (
                <div className="cdb-table__cell flex-item--flex-8 flex-column">{this.state.editedSchedule!.enabled ? moment(this.state.editedSchedule!.nextOccurrenceAt).fromNow() : "Will not run"}
                    <span className="cdb-table__extra-detail">{moment(this.state.editedSchedule!.nextOccurrenceAt).format(FRIENDLY_DATETIME_FORMAT)}</span>
                </div>
            );
        }

    }
    
    private buildActionButton() : JSX.Element
    {
        if(this.state.editEnabled)
        {
            const text = this.state.isNewSchedule ? "Save new" : "Save";
            return (<CommandBarButton iconProps={{iconName: "Save"}} title={text} text={text} ariaLabel={text} disabled={this.state.saveInProgress} onClick={this.saveSchedule}/>);
        }
        else
        {
            return (
                <CommandBarButton iconProps={{iconName: "Edit"}} title="Edit" text="Edit" ariaLabel="Edit" onClick={
                    () => this.setState({
                        editEnabled: true
                    })}
                />);
        }
    }
    
    private buildEnabledCell()
    {
        return(
            <div className="cdb-table__cell flex-item--flex-4">
                <Toggle
                    disabled={!this.state.editEnabled}
                    checked={this.state.editedSchedule!.enabled}
                    onChange={(event,newValue) => {
    
                        const newSchedule = Object.assign({},this.state.editedSchedule,{enabled: newValue});
                        this.setState({
                            editedSchedule: newSchedule
                        });
                    }}
                    role="checkbox"
                />
            </div>
        );
    }

    private buildDeleteCell()
    {
        return (
            <div className={"cdb-table__cell flex-item--flex-1 flex-row flex--align-items-center"}>
                {this.state.isNewSchedule ? null : <IconButton iconProps={{iconName: "Delete"}} title="Delete" ariaLabel="Delete" onClick={this.deleteSchedule}/>}
            </div>
        );
    }
    
    private saveSchedule = async () =>
    {
        
        if(this.state.editedSchedule === undefined)
        {
            return;
        }
        
        if(!this.state.editedSchedule.cronSchedule || this.state.editedSchedule.cronSchedule === "")
        {
            this.props.dispatch(Actions.showMessageBar("Schedule frequency missing",MessageBarType.error));
            return;
        }
        
        if(!this.state.editedSchedule.name || this.state.editedSchedule.name === "")
        {
            this.props.dispatch(Actions.showMessageBar("Workload/Playlist name missing",MessageBarType.error));
            return;
        }
        
        if(!this.state.editedSchedule.schoolCode || this.state.editedSchedule.schoolCode === "" ||
            !this.state.editedSchedule.tenantName || this.state.editedSchedule.tenantName === "")
        {
            this.props.dispatch(Actions.showMessageBar("School missing",MessageBarType.error));
            return;
        }
        
        try
        {
            this.setState({
                saveInProgress: true
            });
            
            await this.props.apiService.putSchoolSchedule(this.state.editedSchedule);
            this.props.dispatch(Actions.showMessageBar("Saved Schedule successfully",MessageBarType.success));
            
            await this.props.onSave();
            
            if(this.state.isNewSchedule)
            {
                this.setState({
                    editedSchedule: createBlankSchedule(this.props.school),
                    editEnabled: true,
                    saveInProgress: false,
                })
            }
            
        }
        catch(err)
        {
            this.props.dispatch(Actions.showMessageBar("Failed to save schedule " + err.message,MessageBarType.error));
            
            this.setState({
                saveInProgress: false
            });
        }
    };
    
    private deleteSchedule = async () => {
        
        if(this.state.editedSchedule === undefined)
        {
            return;
        }
        
        try
        {
            this.setState({
                saveInProgress: true
            });

            await this.props.apiService.deleteSchoolSchedule(this.state.editedSchedule);
            this.props.dispatch(Actions.showMessageBar("Deleted schedule",MessageBarType.success));

            await this.props.onSave();
        }
        catch(err)
        {
            this.props.dispatch(Actions.showMessageBar("Failed to delete schedule " + err.message,MessageBarType.error));

            this.setState({
                saveInProgress: false
            });
        }
    }
    
    public render() {
        
        if(this.state.editedSchedule === undefined)
        {
            return null;
        }
        
        return (
            <div className={`cdb-table__row ${this.props.className ? this.props.className : ""}`}>
                
                {this.buildNameCell()}

                {this.buildCronStringCell()}
                {this.buildLastExecutionCell()}
                {this.buildNextExecutionCell()}
                {this.buildEnabledCell()}
                
                <div className="cdb-table__cell flex-item--flex-4">
                    {this.buildActionButton()}
                </div>
                
                {this.buildDeleteCell()}
            </div>
        );
    }
}

export default connect((state: IRootState,ownProps: {schedule?: SchoolSchedule, school: School,workloadAndPlaylistOptions: IDropdownOption[], className?: string, namesWithExistingSchedules: string[], onSave(): Promise<void>}): SchoolScheduleListRowProps => {
    
    return {
        school: ownProps.school,
        schedule: ownProps.schedule,
        className: ownProps.className,
        namesWithExistingSchedules: ownProps.namesWithExistingSchedules,
        onSave: ownProps.onSave,
        
        apiService: state.auth.apiService,
        workloadAndPlaylistOptions: ownProps.workloadAndPlaylistOptions
    };
})(SchoolScheduleListRow);